import React from "react";
import { useNavigate } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { formatTimeAgo } from "helpers/Datetime.helper";
import type { IWalletHistoryItem } from "api/student/v2/wallet.api";

interface StadiaComponentProps {
    balance: number | null;
    isBalanceLoading: boolean;
    list: IWalletHistoryItem[];
    pagination: { page: number; limit: number; totalCount: number };
    isLoading: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
}

const StadiaComponent: React.FC<StadiaComponentProps> = ({
    balance,
    isBalanceLoading,
    list,
    pagination,
    isLoading,
    isLoadingMore,
    onLoadMore,
}) => {
    const navigate = useNavigate();
    const hasMore = list.length < pagination.totalCount;

    // coinOrderStatus 자식 코드: coinOrderStatus:cancelled | created | failed | paid | refunded → 다국어
    const getStatusLabel = (code: string) => {
        const statusKeyMap: Record<string, string> = {
            "coinOrderStatus:paid": "statusPaid",
            "coinOrderStatus:cancelled": "statusCancelled",
            "coinOrderStatus:refunded": "statusRefunded",
            "coinOrderStatus:created": "statusCreated",
            "coinOrderStatus:failed": "statusFailed",
        };
        const key = statusKeyMap[code];
        if (key) {
            const label = getLanguageValue(PAGE_TYPE.STUDENT, `myStadia.${key}`);
            if (label) return label;
        }
        return code || "-";
    };

    const getStatusColor = (code: string) => {
        switch (code) {
            case "coinOrderStatus:paid":
                return "text-green-600";
            case "coinOrderStatus:cancelled":
            case "coinOrderStatus:failed":
                return "text-gray-500";
            case "coinOrderStatus:refunded":
                return "text-amber-600";
            case "coinOrderStatus:created":
                return "text-blue-600";
            default:
                return "text-gray-700";
        }
    };

    return (
        <main className="bg-gray-50 min-h-screen py-6">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4 mb-6">
                    <button
                        type="button"
                        onClick={() => navigate("/student/my")}
                        className="text-gray-600 hover:text-gray-900"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "myStadia.txtTitle") || "스타디아 잔액·내역"}
                    </h1>
                </div>

                {/* 잔액 카드 */}
                <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                    <p className="text-gray-500 text-sm mb-1">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "my.txtSectionStadiaBalanceLabel") || "현재 잔액"}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                        {isBalanceLoading ? "..." : (balance !== null ? Number(balance).toLocaleString("ko-KR") : "-")}
                    </p>
                </section>

                {/* 내역 (스타디아 추가 + 스타디아 사용) */}
                <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "myStadia.txtHistoryTitle") || "내역"}
                        </h2>
                    </div>
                    <div className="p-4">
                        {isLoading ? (
                            <div className="space-y-3">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="animate-pulse flex justify-between items-center py-3 border-b border-gray-100">
                                        <div className="h-4 bg-gray-200 rounded w-1/3" />
                                        <div className="h-4 bg-gray-200 rounded w-16" />
                                    </div>
                                ))}
                            </div>
                        ) : list.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                <p className="text-center">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "myStadia.txtNoHistory") || "내역이 없습니다."}
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {list.map((item) => (
                                    <div
                                        key={item.id}
                                        className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {item.type === "credit"
                                                    ? (getLanguageValue(PAGE_TYPE.STUDENT, "myStadia.txtCreditLabel") || "스타디아 추가")
                                                    : (item.orderTitle || getLanguageValue(PAGE_TYPE.STUDENT, "myStadia.txtDebitLabel") || "스타디아 사용")}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {formatTimeAgo(item.date)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            {item.type === "debit" && item.coinOrderStatusCode != null && (
                                                <span className={`text-sm font-medium ${getStatusColor(item.coinOrderStatusCode)}`}>
                                                    {getStatusLabel(item.coinOrderStatusCode)}
                                                </span>
                                            )}
                                            <span className={`text-sm font-semibold ${item.type === "credit" ? "text-green-600" : "text-gray-900"}`}>
                                                {item.type === "credit" ? "+" : "-"}
                                                {Number(item.amount).toLocaleString("ko-KR")}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {!isLoading && list.length > 0 && hasMore && (
                            <div className="pt-4 text-center">
                                <button
                                    type="button"
                                    onClick={onLoadMore}
                                    disabled={isLoadingMore}
                                    className="py-2 px-4 text-sm text-[#CF3688] hover:text-[#B82F77] font-medium disabled:opacity-50"
                                >
                                    {isLoadingMore
                                        ? (getLanguageValue(PAGE_TYPE.STUDENT, "myStadia.btnLoadingMore") || "불러오는 중...")
                                        : (getLanguageValue(PAGE_TYPE.STUDENT, "myStadia.btnLoadMore") || "더 보기")}
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </main>
    );
};

export default StadiaComponent;
